import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import { cn } from "@/libs/utils";
import { useIsFetching } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { ReactNode } from "react";
import { Link } from "react-router-dom";

type SyntheseTabKey = "dashboard" | "financeurs" | "intervenants" | "projets";

const TABS: Array<{
  key: SyntheseTabKey;
  label: string;
  to: string;
}> = [
  {
    key: "dashboard",
    label: "Tableau de bord",
    to: "/dashboard/syntheses/tableau-de-bord",
  },
  {
    key: "financeurs",
    label: "Financeurs",
    to: "/dashboard/syntheses/financeurs",
  },
  {
    key: "intervenants",
    label: "Intervenants",
    to: "/dashboard/syntheses/intervenants",
  },
  {
    key: "projets",
    label: "Projets",
    to: "/dashboard/syntheses/projets",
  },
];

const TAB_LABEL_BY_KEY: Record<SyntheseTabKey, string> = {
  dashboard: "Tableau de bord",
  financeurs: "Financeurs",
  intervenants: "Intervenants",
  projets: "Projets",
};

function SynthesesShell({
  active,
  title,
  description,
  children,
}: {
  active: SyntheseTabKey;
  title: string;
  description: string;
  children: ReactNode;
}) {
  const groupedContractsFetchingCount = useIsFetching({
    predicate: (query) => {
      const endpoint = query.queryKey[1];
      return (
        typeof endpoint === "string" &&
        endpoint.includes("/api/syntheses/grouped-contracts")
      );
    },
  });
  const isUpdatingGroupedContracts = groupedContractsFetchingCount > 0;

  return (
    <div className="space-y-6 rounded-[28px] bg-gradient-to-br from-slate-100 via-slate-50 to-emerald-50/60 p-6">
      <ReactBreadcrumb
        items={[
          { href: "/dashboard", name: "Tableau de bord" },
          { href: "/dashboard/syntheses", name: "Suivi de financements" },
          { name: TAB_LABEL_BY_KEY[active] },
        ]}
        isTruncated={false}
      />

      <div className="rounded-2xl border border-slate-200/80 bg-white/85 px-5 py-5 backdrop-blur-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-[24px] font-medium text-slate-900">{title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {isUpdatingGroupedContracts ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-medium text-sky-700">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Mise à jour des infos...
              </span>
            ) : null}
            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
              Nouveau module
            </span>
          </div>
        </div>

        <div className="mt-6 flex justify-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-2 rounded-2xl border border-emerald-100 bg-gradient-to-r from-emerald-50 via-white to-sky-50 p-2 shadow-sm ring-1 ring-white/70">
            {TABS.map((tab) => (
              <Link
                key={tab.key}
                to={tab.to}
                className={cn(
                  "min-w-[148px] rounded-xl px-5 py-3 text-center text-[15px] font-semibold tracking-[0.01em] transition-all duration-150",
                  active === tab.key
                    ? "bg-white text-emerald-700 shadow-md ring-1 ring-emerald-200"
                    : "text-slate-600 hover:bg-white/80 hover:text-slate-900 hover:shadow-sm",
                )}
              >
                {tab.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {children}
    </div>
  );
}

export default SynthesesShell;
